import { getCoverageLineConfig } from '@common/config';
import { useMemo, useState } from 'react';
import { CoverageLine, DatesRangeType } from 'enums';
import { useLocalStorage, usePagination } from 'hooks';
import { CarrierSubmissionSearchItem } from 'types';
import { SubmissionFilterAttributes } from 'broker/components/SubmissionsView/useSubmissionFilter';
import { submissionDatesRangeConfig } from 'broker/configuration-mappers/submission-dates-range-type-config';
import { QueueFilterType, queueSorters, QueueSortType, queueStorageKeys } from './config';
import { useCoverageLineFilter } from './useCoverageLineFilter';
import { useEffectiveDateFilter } from './useEffectiveDateFilter';
import { useInsuredFilter } from './useInsuredFilter';

export { QueueFilterType } from './config';

interface QueueFiltersResult {
  filters: Record<QueueFilterType, SubmissionFilterAttributes>;
  filteredItems: CarrierSubmissionSearchItem[];
  search: string;
  setSearch: (value: string) => void;
  isFilterApplied: boolean;
  sortType: QueueSortType;
  setSortType: (value: QueueSortType) => void;
  paginationReset: () => void;
  page: number;
  setPage: (value: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (value: number) => void;
}

export default function useQueueFilters(
  items: CarrierSubmissionSearchItem[],
  isLoading: boolean,
  isFetching: boolean,
): QueueFiltersResult {
  const { page, setPage, rowsPerPage, setRowsPerPage, paginationReset } = usePagination();
  const areItemsLoaded = !isFetching && !isLoading;

  const insuredFilter = useInsuredFilter({ items, areItemsLoaded, paginationReset });
  const coverageLineFilter = useCoverageLineFilter({ items, areItemsLoaded, paginationReset });
  const effectiveDateFilter = useEffectiveDateFilter({ paginationReset });

  const [search, setSearch] = useState('');
  const [sortType, setSortType] = useLocalStorage(queueStorageKeys.sort, QueueSortType.ReceivedNewest);

  const filteredItems = useMemo(
    () =>
      items
        .filter((item) => {
          if (insuredFilter.isAllSelected) {
            return true;
          }
          return insuredFilter.filters.some((filter) => filter.key === item.submission?.insuredName && filter.checked);
        })
        .filter((item) => {
          if (coverageLineFilter.isAllSelected) {
            return true;
          }
          return coverageLineFilter.filters.some(
            (filter) => item.submission?.coverageLines?.includes(filter.key as CoverageLine) && filter.checked,
          );
        })
        .filter((item) => {
          if (effectiveDateFilter.isAllSelected) {
            return true;
          }
          return effectiveDateFilter.filters.some((filter) => {
            if (!item.submission?.dueDate) {
              return false;
            }
            return (
              submissionDatesRangeConfig[filter.key as DatesRangeType].filterFn(item.submission.dueDate) &&
              filter.checked
            );
          });
        })
        .filter((item) => {
          if (search.length < 2) {
            return true;
          }
          const q = search.toLowerCase();
          const terms = [
            item.submission?.insuredName,
            item.agentName,
            ...(item.submission?.coverageLines?.map((cl) => getCoverageLineConfig(cl).text) || []),
          ].filter(Boolean);
          return terms.some((term) => term?.toLowerCase().includes(q));
        })
        .sort(queueSorters[sortType].sorter),
    [
      items,
      insuredFilter.isAllSelected,
      insuredFilter.filters,
      coverageLineFilter.isAllSelected,
      coverageLineFilter.filters,
      effectiveDateFilter.isAllSelected,
      effectiveDateFilter.filters,
      search,
      sortType,
    ],
  );

  const filters = {
    [QueueFilterType.Insured]: insuredFilter,
    [QueueFilterType.Lob]: coverageLineFilter,
    [QueueFilterType.EffectiveDate]: effectiveDateFilter,
  };

  const isFilterApplied = Object.values(filters).some((filter) => !filter.isAllSelected);

  return {
    filters,
    filteredItems,
    search,
    setSearch,
    isFilterApplied,
    sortType,
    setSortType,
    paginationReset,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  };
}
